import Document16 from '@carbon/icons-react/es/document/16';
import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import { isNumber } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { SpaceTabId } from 'api/models';
import { SpaceTabDocumentType } from 'api/space/space-tab';
import { RocketDocument16 } from 'components/atoms/icons';
import { EllipsisMiddle } from 'components/atoms/middle-ellipsis';
import { getSpaceTabDetailUrl } from 'utils/urls';
import { ActionMenu } from '../common';
import { Menu } from '../menu-group/menu-group.styles';
import { generateTabMenuKey } from '../menu-group/utils';
import { SpaceTabMoreActions } from '../more-actions';
import { Container, Image, TitleContainer } from './space-tab-menu-item.styles';
import { SpaceTabMenuItemTypes } from './space-tab-menu-item.types';

const DocumentTitle = ({
  title,
  iconSrc,
}: {
  title: string;
  iconSrc?: string;
}) => {
  const [imageError, { setTrue }] = useBoolean(false);

  const arrOfWords = title.trim().split(' ');
  const lastWordCount = arrOfWords[arrOfWords.length - 1].length;

  return (
    <TitleContainer>
      {iconSrc ? (
        <>
          {imageError ? (
            <Document16 />
          ) : (
            <Image
              onError={setTrue}
              preview={false}
              alt={title}
              width={16}
              src={iconSrc}
            />
          )}
        </>
      ) : (
        <RocketDocument16 />
      )}

      <EllipsisMiddle
        suffixCount={lastWordCount > 8 ? 8 : lastWordCount}
        tooltip
        width="144px"
      >
        {title}
      </EllipsisMiddle>
    </TitleContainer>
  );
};

export const SpaceTabMenuItem = ({
  spaceTab,
  spaceId,
  showNewTab,
  privateSpace,
  ...rest
}: SpaceTabMenuItemTypes) => {
  const { spaceTabId, spaceTabTitle } = spaceTab;
  const [, projectId, _spaceId] = spaceId;

  const stringifiedTabId = spaceTabId.toString();

  const iconSrc = useMemo(() => {
    if (Array.isArray(spaceTab.metaData?.links?.icon)) {
      return spaceTab.metaData?.links?.icon[0]?.href;
    }

    return undefined;
  }, [spaceTab]);

  return (
    <Menu.Item key={generateTabMenuKey(_spaceId, stringifiedTabId)} {...rest}>
      <Container>
        <Tooltip title={spaceTabTitle}>
          <Link
            to={getSpaceTabDetailUrl(
              SpaceTabId(projectId, _spaceId, stringifiedTabId),
            )}
            data-cy={`space-tab-${spaceTab.spaceTabTitle}`}
          >
            {showNewTab ? (
              <FormattedMessage id="space_tab.new_tab" />
            ) : (
              <DocumentTitle title={spaceTabTitle ?? '-'} iconSrc={iconSrc} />
            )}
          </Link>
        </Tooltip>
        {!showNewTab && isNumber(spaceTabId) && (
          <ActionMenu className="more-actions">
            <SpaceTabMoreActions
              spaceTab={spaceTab}
              projectId={projectId}
              showEdit={
                spaceTab.spaceTabType !==
                SpaceTabDocumentType.RocketlaneDocument
              }
              privateSpace={privateSpace}
            />
          </ActionMenu>
        )}
      </Container>
    </Menu.Item>
  );
};
